import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleFilled, EyeInvisibleOutlined, EyeOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons'
import { registration, type PhoneSession, type RegisterInput } from '../api'
import AuthShell, { AuthField, BackToSignIn } from '../components/AuthShell'
import UsernameField, { type NameState } from '../components/UsernameField'

type Props = { onDone: () => void; onCancel: () => void }

/**
 * Creating an account.
 *
 * Six fields is a long form on a phone, so it is grouped into three short ones
 * — who you are, how to reach you, how to get back in — rather than presented
 * as a single column of eight controls.
 *
 * Exactly one of the two contact details is proven now; the other can be
 * confirmed later from Settings. Nothing here chooses a public name: the
 * ledger account is minted by the backend, because the order book prints maker
 * names to everyone who opens the exchange.
 */
export default function Register({ onDone, onCancel }: Props) {
  const { t } = useTranslation()
  const [form, setForm] = useState<RegisterInput>({
    username: '', regNo: '', firstName: '', lastName: '', email: '', phone: '', password: '', verify: 'email',
  })
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [account, setAccount] = useState('')
  const [sentTo, setSentTo] = useState('')
  const [phone, setPhone] = useState<PhoneSession | null>(null)
  const [phoneState, setPhoneState] = useState<'PENDING' | 'VERIFIED' | 'EXPIRED'>('PENDING')
  // Which ways of confirming this deployment can actually perform. Offering a
  // channel that cannot work wastes a whole form and reads as a broken product.
  const [can, setCan] = useState({ email: true, phone: true })
  const [nameState, setNameState] = useState<NameState>('empty')

  useEffect(() => {
    registration.options()
      .then(o => {
        setCan(o.confirm)
        setForm(f => ({ ...f, verify: o.confirm.email ? 'email' : 'phone' }))
      })
      .catch(() => { /* keep both on offer; the server still refuses clearly */ })
  }, [])

  const set = (k: keyof RegisterInput) => (v: string) => setForm(f => ({ ...f, [k]: v }))
  // The name has to be known free, not merely typed — submitting while a
  // check is in flight is how someone finds out at the last step.
  const complete = nameState === 'free' && form.regNo && form.firstName && form.lastName
    && form.email && form.phone && form.password

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!complete || busy) return
    setBusy(true); setError('')
    try {
      const r = await registration.submit(form)
      setAccount(r.account)
      if (r.phone) setPhone(r.phone)
      if (r.email) setSentTo(r.email.sent_to)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }

  if (phone) {
    return <PhoneStep session={phone} state={phoneState} onState={setPhoneState} account={account} onDone={onDone} />
  }

  if (account) {
    return (
      <AuthShell title={t('regCheckEmail')} lede={t('regSentTo', { email: sentTo })}>
        <p className="auth-lede muted">{t('regLinkOnce')}</p>
        <button className="auth-submit" type="button" onClick={onDone}>{t('backToSignIn')}</button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      as="form" onSubmit={submit}
      title={t('createAccount')}
      lede={t('createAccountLede')}
      foot={<span className="auth-alt">{t('haveAccount')}{' '}
        <button type="button" className="linkish" onClick={onCancel}>{t('signIn')}</button></span>}
    >
      {error && <div className="auth-error" role="alert"><WarningOutlined /> <span>{error}</span></div>}

      <section className="auth-group">
        <h3>{t('groupIdentity')}</h3>
        <UsernameField value={form.username} onChange={set('username')} onState={setNameState} />
        <AuthField id="r-reg" label={t('regNo')} hint={t('regNoHint')}
          value={form.regNo} onChange={set('regNo')} autoComplete="off" autoCapitalize="characters" />
        <div className="field-row">
          <AuthField id="r-first" label={t('firstName')} value={form.firstName}
            onChange={set('firstName')} autoComplete="given-name" />
          <AuthField id="r-last" label={t('lastName')} value={form.lastName}
            onChange={set('lastName')} autoComplete="family-name" />
        </div>
      </section>

      <section className="auth-group">
        <h3>{t('groupContact')}</h3>
        <AuthField id="r-email" label={t('email')} type="email" value={form.email}
          onChange={set('email')} autoComplete="email" inputMode="email" />
        <AuthField id="r-phone" label={t('phone')} hint={t('phoneHint')} value={form.phone}
          onChange={set('phone')} autoComplete="tel" inputMode="tel" />

        <fieldset className="choice">
          <legend>{t('confirmWith')}</legend>
          <div className="choice-row">
            {(['email', 'phone'] as const).map(c => (
              <button key={c} type="button" disabled={!can[c]}
                className={form.verify === c ? 'on' : ''}
                aria-pressed={form.verify === c}
                title={can[c] ? undefined : t('channelUnavailable')}
                onClick={() => setForm(f => ({ ...f, verify: c }))}>
                {c === 'email' ? t('email') : t('phone')}
              </button>
            ))}
          </div>
          <span className="field-hint">
            {can.email && can.phone ? t('confirmOneHint') : t('channelUnavailable')}
          </span>
        </fieldset>
      </section>

      <section className="auth-group">
        <h3>{t('groupSecurity')}</h3>
        <AuthField id="r-pass" label={t('password')} hint={t('passwordHint')}
          value={form.password} onChange={set('password')}
          type={show ? 'text' : 'password'} autoComplete="new-password">
          <button type="button" className="icon-btn" onClick={() => setShow(s => !s)}
            aria-label={show ? t('hidePassword') : t('showPassword')}>
            {show ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </button>
        </AuthField>
      </section>

      <button className="auth-submit" type="submit" disabled={busy || !complete}>
        {busy ? <LoadingOutlined /> : t('createAccount')}
      </button>
    </AuthShell>
  )
}

/**
 * The verify.mn step.
 *
 * verify.mn's instruction is shown exactly as given: it names the number the
 * message must be sent from, and sending from a second handset is the usual
 * way this fails. Every send costs the user 150₮ whether or not it matches, so
 * the send action disappears the moment the session reads VERIFIED, and
 * polling stops with it.
 */
function PhoneStep({ session, state, onState, account, onDone }: {
  session: PhoneSession
  state: 'PENDING' | 'VERIFIED' | 'EXPIRED'
  onState: (s: 'PENDING' | 'VERIFIED' | 'EXPIRED') => void
  account: string
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [left, setLeft] = useState(() => secondsLeft(session.expires_at))
  const stop = useRef(false)

  useEffect(() => {
    stop.current = false
    const poll = setInterval(async () => {
      if (stop.current) return
      try {
        const r = await registration.checkPhone(session.session_id)
        if (r.verified) { stop.current = true; onState('VERIFIED'); return }
        if (r.status === 'EXPIRED') { stop.current = true; onState('EXPIRED') }
      } catch { /* transient — keep asking until the session expires */ }
    }, 3000)
    const clock = setInterval(() => {
      const s = secondsLeft(session.expires_at)
      setLeft(s)
      if (s <= 0 && !stop.current) { stop.current = true; onState('EXPIRED') }
    }, 1000)
    return () => { stop.current = true; clearInterval(poll); clearInterval(clock) }
  }, [session.session_id, session.expires_at, onState])

  if (state === 'VERIFIED') {
    return (
      <AuthShell title={t('phoneConfirmed')} lede={t('accountReady', { account })}>
        <p className="verified-mark"><CheckCircleFilled /></p>
        <button className="auth-submit" type="button" onClick={onDone}>{t('signIn')}</button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t('confirmPhone')} lede={session.instruction}
      foot={<BackToSignIn onClick={onDone} />}>
      <div className="sms-code">
        <span className="sms-label">{t('sendThis')}</span>
        <b className="num">{session.code}</b>
        <span className="sms-label">{t('toShortcode', { shortcode: session.shortcode })}</span>
      </div>
      {state === 'EXPIRED' ? (
        <div className="auth-error" role="alert"><WarningOutlined /> <span>{t('codeExpired')}</span></div>
      ) : (
        <>
          <a className="auth-submit as-link" href={session.sms_uri}>{t('openMessages')}</a>
          <p className="auth-lede muted">{t('smsCost')}</p>
          <p className="auth-lede muted"><LoadingOutlined /> {t('waitingForSms', { seconds: left })}</p>
        </>
      )}
    </AuthShell>
  )
}

/**
 * Where the emailed confirmation link lands. Reachable without a token by
 * design — the whole point is that the person has not signed in yet.
 */
export function VerifyEmail({ token, onDone }: { token: string; onDone: () => void }) {
  const { t } = useTranslation()
  const [state, setState] = useState<'busy' | 'ok' | 'bad'>('busy')
  const [account, setAccount] = useState('')
  const [error, setError] = useState('')
  const once = useRef(false)

  useEffect(() => {
    // The link is single-use, so React's development double-invoke would spend
    // it and report failure on the second call.
    if (once.current) return
    once.current = true
    registration.confirmEmail(token)
      .then(r => { setAccount(r.account); setState('ok') })
      .catch(e => { setError(e instanceof Error ? e.message : String(e)); setState('bad') })
  }, [token])

  return (
    <AuthShell
      title={state === 'ok' ? t('emailConfirmed') : t('confirming')}
      lede={state === 'ok' ? t('accountReady', { account }) : undefined}
    >
      {state === 'busy' && <p className="auth-lede"><LoadingOutlined /> {t('confirming')}</p>}
      {state === 'ok' && <p className="verified-mark"><CheckCircleFilled /></p>}
      {state === 'bad' && (
        <div className="auth-error" role="alert"><WarningOutlined /> <span>{error}</span></div>
      )}
      {state !== 'busy' && (
        <button className="auth-submit" type="button" onClick={onDone}>{t('signIn')}</button>
      )}
    </AuthShell>
  )
}

function secondsLeft(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 1000)) : 0
}
